import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart } from '../../components/charts';
import { DollarSign, Users, Target, TrendingUp, Image, Video, ShoppingBag, Loader2 } from 'lucide-react';
import AccountSwitcher from '../../components/AccountSwitcher';
import { useAccounts } from '../../context/AccountContext';

const performanceData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  datasets: [{
    label: 'Facebook',
    data: [1500, 2200, 2100, 2800, 2600, 3200],
    borderColor: 'rgb(59, 89, 152)',
    backgroundColor: 'rgba(59, 89, 152, 0.1)',
    fill: true,
  }, {
    label: 'Instagram',
    data: [1000, 1400, 1600, 1800, 2100, 2400],
    borderColor: 'rgb(193, 53, 132)',
    backgroundColor: 'rgba(193, 53, 132, 0.1)',
    fill: true,
  }]
};

const adFormatPerformance = {
  labels: ['Image Ads', 'Video Ads', 'Stories', 'Carousel'],
  datasets: [{
    data: [40, 30, 20, 10],
    backgroundColor: [
      'rgb(59, 89, 152)',
      'rgb(193, 53, 132)',
      'rgb(81, 91, 212)',
      'rgb(247, 119, 55)'
    ],
  }]
};

export default function MetaAds() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        // selectedAccountId intentionally used for future per-account filtering
        
        // Direkt API endpoint'e istek at
        const response = await fetch('https://graph.facebook.com/v18.0/me/adaccounts', {
          params: {
            access_token: 'EAAGstZAws7yQBOzwb2vOUHeQHj1VfaZBmkoAoJUEdpVGOPy1RUqjbE29mKVmGnkgKojveeFfV4j0ZAGgB8b4iazenrwaANo0zWDNpbm19pjT2v8eZARlvkQVADOMZA87ciZAlAC5359iqQc1PsF9Nr1xogMANPDqOcZCTupey439sGvQ1efZCIhP3QZDZD',
            fields: 'name,account_id,account_status,currency,business_name,balance',
            limit: 100
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch accounts');
        }

        const data = await response.json();
        console.log('Meta API Response:', data);
        setAccounts(data.data || []);

      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="text-gray-600 text-lg animate-pulse">
            Loading Meta Ads accounts...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-6 bg-red-50 border-red-100">
          <h2 className="text-lg font-medium text-red-700">Error</h2>
          <p className="text-red-600 mt-1">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Meta Ads Accounts</h1>
        <AccountSwitcher platform="META_ADS" />
      </div>

      <div className="grid gap-4">
        {accounts.map(account => (
          <Card key={account.id} className="p-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-gray-500">Account Name</p>
                <p className="font-medium">{account.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Account ID</p>
                <p className="font-medium">{account.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className={`font-medium ${
                  account.account_status === 1 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {account.account_status === 1 ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Business Name</p>
                <p className="font-medium">{account.business_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Balance</p>
                <p className="font-medium">
                  {(account.balance / 100).toFixed(2)} {account.currency}
                </p>
              </div>
            </div>
          </Card>
        ))}

        {accounts.length === 0 && (
          <Card className="p-6 bg-gray-50">
            <p className="text-center text-gray-600">No ad accounts found</p>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Spend"
          value="$32,456"
          change="+15.2%"
          icon={<DollarSign />}
        />
        <StatCard
          title="Reach"
          value="2.8M"
          change="+22.4%"
          icon={<Users />}
        />
        <StatCard
          title="Conversions"
          value="10,234"
          change="+18.7%"
          icon={<Target />}
        />
        <StatCard
          title="ROAS"
          value="3.8x"
          change="+5.2%"
          icon={<TrendingUp />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card className="p-6 rounded-2xl">
            <h2 className="text-lg font-semibold mb-4">Platform Performance</h2>
            <LineChart data={performanceData} />
          </Card>
        </div>
        <div>
          <Card className="p-6 rounded-2xl">
            <h2 className="text-lg font-semibold mb-4">Ad Format Distribution</h2>
            <BarChart data={adFormatPerformance} />
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Top Performing Ad Sets</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Ad Set</th>
                  <th className="text-left py-3">Reach</th>
                  <th className="text-left py-3">CTR</th>
                  <th className="text-left py-3">CPC</th>
                </tr>
              </thead>
              <tbody>
                <AdSetRow
                  name="Retargeting Campaign"
                  reach="450K"
                  ctr="3.8%"
                  cpc="$0.52"
                />
                <AdSetRow
                  name="Lookalike Audience"
                  reach="380K"
                  ctr="2.9%"
                  cpc="$0.48"
                />
                <AdSetRow
                  name="Interest-Based"
                  reach="290K"
                  ctr="2.5%"
                  cpc="$0.45"
                />
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Ad Formats</h2>
          <div className="space-y-4">
            <AdFormat
              title="Image Ads"
              icon={<Image className="w-5 h-5" />}
              stats={{
                spend: "$15,678",
                conversions: "567",
                roas: "3.5x"
              }}
            />
            <AdFormat
              title="Video Ads"
              icon={<Video className="w-5 h-5" />}
              stats={{
                spend: "$10,234",
                conversions: "345",
                roas: "3.2x"
              }}
            />
            <AdFormat
              title="Carousel Ads"
              icon={<ShoppingBag className="w-5 h-5" />}
              stats={{
                spend: "$6,543",
                conversions: "234",
                roas: "2.8x"
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ title, value, change, icon }) {
  const isPositive = change?.startsWith('+');
  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{value}</p>
            {change && (
              <span className={`text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {change}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function AdSetRow({ name, reach, ctr, cpc }) {
  return (
    <tr className="border-b">
      <td className="py-3">{name}</td>
      <td className="py-3">{reach}</td>
      <td className="py-3">{ctr}</td>
      <td className="py-3">{cpc}</td>
    </tr>
  );
}

function AdFormat({ title, icon, stats }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
          {icon}
        </div>
        <span className="font-medium">{title}</span>
      </div>
      <div className="flex gap-6">
        <div>
          <p className="text-sm text-gray-600">Spend</p>
          <p className="font-medium">{stats.spend}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Conv.</p>
          <p className="font-medium">{stats.conversions}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">ROAS</p>
          <p className="font-medium">{stats.roas}</p>
        </div>
      </div>
    </div>
  );
} 